// Do two lessons land on top of each other?
//
// MMS checks, and offers it as a studio setting — Business Settings → General
// → Event Scheduling:
//
//   [x] Check for scheduling conflicts when adding/editing calendar events
//
// The setting lives in domain/studio.js as `scheduling.checkConflicts`; this
// module is the detection behind it. Pure: it runs before a save, and a test
// pins it.
//
// **The rules are `core/src/conflicts.rs`'s and this file delegates to them**
// (#10, #214): `findConflicts` below calls `coreSync().findConflicts`. There is
// one implementation, so no golden corpus; the recorded answers are
// `core/src/conflicts_vectors.json`.
//
// ── Four rules worth reading before the code ─────────────────────────────────
//
// 1. A conflict is a PERSON in two places, not two events on one grid. MMS's
//    dialog names the attendee who is double-booked — "<name> is already
//    attending another event at this time on <date>" — and names the
//    *teacher* on a non-teaching event with no attendees at all: the teacher
//    attends everything, so any overlap is a conflict and an empty attendee
//    list does not excuse one. Two students collide only where they share a
//    lesson. So detection is by time (below) and the *report* carries who is
//    double-booked — see `doubleBooked` on each conflict.
//
// 2. Overlap is by TIME, not by date. Two 30-minute lessons at 16:00 and 16:30
//    on the same afternoon are a normal teaching day, not a conflict; 16:00 for
//    45 minutes and 16:30 for 30 is a double booking. Occupancy is treated as
//    the half-open interval [start, start + duration), so lessons that merely
//    touch — one ending exactly as the next begins — do not conflict, so
//    back-to-back lessons are allowed.
//
// 3. A bounded horizon, stated rather than hidden. "Do these two series ever
//    collide" is not answerable for two series that repeat indefinitely: there
//    is no last occurrence to walk to. So the comparison runs over a window of
//    CONFLICT_HORIZON_DAYS from the proposal's own first occurrence, and the
//    result says so — `horizon` names the window and `beyondHorizon` is true
//    when the proposal itself keeps producing entries past the end of it. A
//    caller that prints "no conflicts" without reading those is printing "no
//    conflicts before <date>" and should say which.
//
// 4. A series never conflicts with itself. An edit is a *replacement* of what is
//    already stored, so the stored version of the thing being edited is excluded
//    from the comparison — otherwise every edit reports the event colliding with
//    the event.
//
// ── A fifth, not implemented (#313) ──────────────────────────────────────────
//
// 5. **An all-day event should occupy no time and conflict with nothing**, as
//    `price_occurrence` derives nothing from the blanked fields for money
//    (#322). MMS's answer is not known (#286); the argument is at the head of
//    `core/src/conflicts.rs`.
//
//    The conflict check's schedules carry no `allDay` — not `Schedule` in
//    core/src/conflicts.rs, not `schedulesOf` in src/domain/schedule.js, not
//    `flattenSchedule` below — so an all-day event occupies its stored
//    `startTime` and `durationMin`, which `allDay` blanks on the form and
//    leaves on the document.

// `./core.js`, not `../lib/core.js`: this directory is copied verbatim into
// functions/domain/ and the door is inside the copy (#218).
import { coreSync } from './core.js';

// How far ahead two indefinite series are compared: one year.
//
// Schedules are built in terms and school years, so a year covers the
// collisions a teacher is scheduling towards. The check runs on every save, so
// the window slides forward each time either series is touched.
//
// A walk over a window rather than solving for the first collision
// analytically, which fails once a rule is monthly and the stride is not a
// fixed number of days (see `strideOf` and the end-of-month clamping in
// recurrence.js).
export const CONFLICT_HORIZON_DAYS = 365;

// How many collisions to name. Two weekly series that collide collide every
// week, so MMS states the count separately from the list — "Scheduling
// Conflicts: 1" as a heading above the bullets — and this names a sample.
export const CONFLICT_SAMPLE = 3;

// ── The occupancy arithmetic is the core's (#214) ──────────────────────────
//
// `dayIndex`, `minutesOfDay`, `spanOf` and the rest are
// `core/src/conflicts.rs`'s. Two of their rules:
//
//   * **occupancy is absolute minutes on the UTC calendar grid, not a time of
//     day.** That makes an event running past midnight compare correctly
//     against the next morning with no special case, and it keeps the overlap
//     test one line that cannot disagree with itself about which day it is on.
//   * **a time that cannot be read is not midnight.** `minutesOfDay` answers
//     null for anything that is not `HH:MM`, and the occurrence is dropped
//     from the comparison rather than placed at 00:00.
//
// `core/src/conflicts_vectors.json` holds the cases for both.

// One series, dated, over a window — with its per-entry deviations folded in.
//
// A *schedule* is the rule plus the overrides that deviate from it:
//
//   {
//     seriesId, startDate, startTime, durationMin,
//     frequency, interval, endDate, exceptions,
//     categoryId, categoryLabel, studentIds,  // carried so a conflict can name
//                                             // people, and a reminder can find
//                                             // the category's lead time
//     overrides: { [ruleDate]: { movedDate, startTime, durationMin,
//                                categoryId, categoryLabel, studentIds } },
//   }
//
// `categoryId` is not read by the conflict report, but this fold is also the
// reminder worker's, and the lead time a reminder obeys hangs off the
// category *record*, which a label cannot find (#185).
//
// ── The rule is `coda_domain::conflicts::occurrences_of` (#214) ─────────────
//
// `{ occurrences, truncated }` is a list inside a record, which the flat rule
// refuses. So it crosses as three calls over one fold — the lessons, who is in
// them, and the flag — and the rejoin below makes them one answer again, as
// `findConflicts` does.
//
// **What crosses in is this schedule, not the documents behind it.** A
// schedule reaching here has been through `schedulesOf` already or come
// straight off a form (`singleEntrySchedule`), and the stored side's defaults
// must not be applied a second time: `categoryLabel` is `?? null` in this fold
// and `|| 'Lesson'` in that projection, so a proposal that carries no label
// would come back in a category nobody chose.
//
// The fold is the one `occurrencesBetween` in domain/schedule.js does, but
// that returns a full calendar projection of every series keyed by occurrence
// id, and this answers about one schedule and reports truncation. The three
// rules both obey:
//
//   * a removed occurrence is not produced at all — the rule date is in
//     `exceptions` and the expansion skips it — so there is nothing here to
//     decide. An occurrence whose *attendance* says Teacher Cancelled still
//     occupies its slot: it is still on the calendar, and this module is not
//     handed attendance records. See the note in domain/schedule.js for why
//     those are the only two states;
//   * a moved occurrence is taught on its *effective* date, not its rule date,
//     and conflicts there;
//   * a move whose rule date the rule no longer produces is unreachable, so it
//     is dropped rather than reported against a lesson not on the calendar.

/** One schedule, split into the boundary's record and its four lists. */
const flattenSchedule = (schedule) => {
  const overrides = [];
  const overrideStudents = [];
  const seriesId = schedule?.seriesId ?? null;
  for (const [ruleDate, ov] of Object.entries(schedule?.overrides || {})) {
    overrides.push({
      seriesId: seriesId ?? '',
      ruleDate,
      movedDate: ov?.movedDate ?? null,
      startTime: ov?.startTime ?? null,
      durationMin: num(ov?.durationMin),
      categoryId: ov?.categoryId ?? null,
      categoryLabel: ov?.categoryLabel ?? null,
      setsStudentIds: Array.isArray(ov?.studentIds),
    });
    for (const sid of ov?.studentIds || []) {
      if (sid) overrideStudents.push({ seriesId: seriesId ?? '', ruleDate, studentId: sid });
    }
  }
  return [
    {
      seriesId,
      startDate: schedule?.startDate ?? null,
      startTime: schedule?.startTime ?? null,
      durationMin: num(schedule?.durationMin),
      frequency: schedule?.frequency ?? null,
      interval: num(schedule?.interval),
      endDate: schedule?.endDate ?? null,
      byWeekday: num(schedule?.byWeekday),
      weekOfMonth: num(schedule?.weekOfMonth),
      categoryId: schedule?.categoryId ?? null,
      categoryLabel: schedule?.categoryLabel ?? null,
    },
    (schedule?.exceptions || []).filter((d) => typeof d === 'string' && d),
    (schedule?.studentIds || []).filter(Boolean),
    overrides,
    overrideStudents,
  ];
};

export function occurrencesOf(schedule, from, to) {
  const a = [...flattenSchedule(schedule), from, to];
  const rows = coreSync().scheduleOccurrences(...a);
  const attendees = coreSync().scheduleOccurrenceAttendees(...a);

  // Keyed by position, which is what the boundary returns: both calls run the
  // same fold and the same sort, so index `i` is the same lesson in each.
  const byIndex = new Map();
  for (const { occurrenceIndex, studentId } of attendees) {
    const list = byIndex.get(occurrenceIndex) || [];
    list.push(studentId);
    byIndex.set(occurrenceIndex, list);
  }

  return {
    occurrences: rows.map((o, i) => ({
      seriesId: o.seriesId ?? null,
      ruleDate: o.ruleDate,
      date: o.date,
      // `undefined` rather than `null` where no document supplies one, the
      // shape `ov?.startTime || schedule.startTime` gives. `spanOf` refuses it
      // either way — an occurrence whose time cannot be read is left out
      // rather than treated as midnight.
      startTime: o.startTime,
      durationMin: o.durationMin,
      categoryId: o.categoryId ?? null,
      categoryLabel: o.categoryLabel ?? null,
      studentIds: byIndex.get(i) || [],
      span: { start: o.spanStart, end: o.spanEnd },
    })),
    // Its own call because it is not derivable from the rows: the cap is on the
    // expansion *before* the window filter drops lessons moved out, so counting
    // what came back would under-report it.
    truncated: coreSync().scheduleOccurrencesTruncated(...a),
  };
}

// ── Shaping a nested schedule into the boundary's five lists (#214) ────────
//
// The core takes the calendar as a **join**: series, their exceptions, their
// students, overrides, and overrides' students, each a flat list keyed back to
// its parent. A JavaScript schedule is the same data nested — `exceptions` is
// an array on the series and `overrides` is a **map keyed by rule date**.
// Converting between the two is shaping, not a rule.
//
// **One thing in here is a rule and lives in the core**: whether an override
// is identified well enough to join a series — `seriesId && date` — crosses as
// `coreSync().overrideKey` (#214).

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** One schedule's five rows, keyed back to it. */
const flattenSchedules = (schedules) => {
  const series = [];
  const exceptions = [];
  const seriesStudents = [];
  const overrides = [];
  const overrideStudents = [];

  for (const sch of schedules || []) {
    if (!sch) continue;
    const id = sch.seriesId ?? null;
    series.push({
      id: id ?? '',
      startDate: sch.startDate ?? null,
      startTime: sch.startTime ?? null,
      durationMin: num(sch.durationMin),
      frequency: sch.frequency ?? null,
      interval: num(sch.interval),
      endDate: sch.endDate ?? null,
      byWeekday: num(sch.byWeekday),
      weekOfMonth: num(sch.weekOfMonth),
      categoryId: sch.categoryId ?? null,
      categoryLabel: sch.categoryLabel ?? null,
      pricingRule: sch.pricingRule ?? null,
      priceOverrideMinor: num(sch.priceOverrideMinor),
    });
    for (const d of sch.exceptions || []) {
      if (typeof d === 'string' && d) exceptions.push({ seriesId: id ?? '', date: d });
    }
    for (const sid of sch.studentIds || []) {
      if (sid) seriesStudents.push({ seriesId: id ?? '', studentId: sid });
    }
    for (const [ruleDate, ov] of Object.entries(sch.overrides || {})) {
      // The rule, not a shape — see the note above.
      if (!coreSync().overrideKey(id, ruleDate)) continue;
      overrides.push({
        id: `${id}__${ruleDate}`,
        seriesId: id ?? '',
        date: ruleDate,
        movedDate: ov?.movedDate ?? null,
        startTime: ov?.startTime ?? null,
        durationMin: num(ov?.durationMin),
        categoryId: ov?.categoryId ?? null,
        categoryLabel: ov?.categoryLabel ?? null,
        pricingRule: ov?.pricingRule ?? null,
        priceOverrideMinor: num(ov?.priceOverrideMinor),
        // The load-bearing flag: an override with no `studentIds` inherits the
        // schedule's, one with an empty list is a lesson with nobody in it, and
        // a join table contributes zero rows for both.
        setsStudentIds: Array.isArray(ov?.studentIds),
      });
      for (const sid of ov?.studentIds || []) {
        if (sid) overrideStudents.push({ overrideId: `${id}__${ruleDate}`, studentId: sid });
      }
    }
  }
  return { series, exceptions, seriesStudents, overrides, overrideStudents };
};

// The proposal, which is one schedule and travels as its own arguments.
//
// ── The proposal's overrides are carried (#334) ────────────────────────────
//
// A proposal expanded as though no entry of it had ever been moved, re-timed
// or emptied would check a save that rewrites a rule against each entry's
// **rule date** rather than the day it is taught. `ruleSchedule` in
// `src/hooks/useEventForm.js` builds the proposal with
// `conflictSchedules.find(…)?.overrides` — *"the calendar as it stands: a
// moved entry collides where it moved to"*.
//
// `core/src/conflicts_vectors.json` case 18 of the `findConflictsForCalendar`
// cases changes its answer if this drops the proposal's overrides. Only this
// path would show it: `core/src/conflicts.rs` builds the `Schedule` directly,
// `core-ffi/tests/boundary.rs` hand-writes `&[]` for both arguments, and
// `test/domain/conflicts.test.js`' `proposal()` helper spreads a schedule
// whose `overrides` is `{}`.
//
// Keyed by rule date alone, with no `overrideKey` check — unlike a stored
// override, which has to be joined to a series and so has to name one. The
// proposal *is* the series, so its map's key is the whole identity, which is
// how `CoreVectorTest.proposalOf` reads the same vectors.
const flattenProposed = (p) => {
  const overrides = [];
  const overrideStudents = [];
  for (const [ruleDate, ov] of Object.entries(p?.overrides || {})) {
    if (typeof ruleDate !== 'string' || !ruleDate) continue;
    overrides.push({
      ruleDate,
      movedDate: ov?.movedDate ?? null,
      startTime: ov?.startTime ?? null,
      durationMin: num(ov?.durationMin),
      categoryLabel: ov?.categoryLabel ?? null,
      // The same load-bearing flag a stored override carries, for the same
      // reason: one with no `studentIds` inherits the schedule's, one with an
      // empty list is a lesson with nobody in it, and a join table contributes
      // zero rows for both.
      setsStudentIds: Array.isArray(ov?.studentIds),
    });
    for (const sid of ov?.studentIds || []) {
      if (sid) overrideStudents.push({ ruleDate, studentId: sid });
    }
  }
  return {
    proposed: {
      seriesId: p?.seriesId ?? null,
      startDate: p?.startDate ?? null,
      startTime: p?.startTime ?? null,
      durationMin: num(p?.durationMin),
      frequency: p?.frequency ?? null,
      interval: num(p?.interval),
      endDate: p?.endDate ?? null,
      byWeekday: num(p?.byWeekday),
      weekOfMonth: num(p?.weekOfMonth),
      categoryLabel: p?.categoryLabel ?? null,
    },
    exceptions: (p?.exceptions || []).filter((d) => typeof d === 'string' && d),
    studentIds: (p?.studentIds || []).filter(Boolean),
    overrides,
    overrideStudents,
  };
};

/** The core answers flat; `ConflictDialog` reads nested. Rejoin, once, here. */
const rejoin = (summary, sample, doubleBooked) => {
  const byIndex = new Map();
  for (const d of doubleBooked) {
    const list = byIndex.get(d.conflictIndex) || [];
    list.push(d.studentId);
    byIndex.set(d.conflictIndex, list);
  }
  // Key order is cosmetic to every consumer and not to `golden/generate.js`,
  // which records insertion order, so it stays fixed.
  return {
    count: summary.count,
    conflicts: sample.map((c, i) => ({
      date: c.date,
      startTime: c.startTime ?? null,
      durationMin: c.durationMin,
      doubleBooked: { teacher: c.doubleBookedTeacher, studentIds: byIndex.get(i) || [] },
      with: {
        seriesId: c.withSeriesId ?? null,
        ruleDate: c.withRuleDate,
        date: c.withDate,
        startTime: c.withStartTime ?? null,
        durationMin: c.withDurationMin,
        categoryLabel: c.withCategoryLabel ?? null,
      },
    })),
    horizon: { start: summary.horizonStart ?? null, end: summary.horizonEnd ?? null },
    beyondHorizon: summary.beyondHorizon,
    truncated: summary.truncated,
  };
};

// Does the schedule this save would create overlap anything already on the
// calendar?
//
// `proposed`   the schedule as it will be after the save — a one-time event is
//              a schedule ending the day it starts, and "this entry only" is a
//              schedule one day long at the day the form is showing
//              (`singleEntrySchedule` below builds it).
// `schedules`  every series already stored, in the same shape.
// `ignore`     what of the stored schedule this proposal replaces, so it is not
//              reported as conflicting with itself:
//                { seriesId }            the whole series — a save to the rule
//                { seriesId, ruleDate }  one entry of it — "this entry only",
//                                        which leaves the *rest* of the series
//                                        in the comparison, because moving this
//                                        Tuesday onto a Wednesday its own series
//                                        already occupies is a real conflict.
//              Defaults to the whole of `proposed.seriesId`, which is right for
//              every save that rewrites a rule.
//
// Returns:
//   { count, conflicts, horizon: { start, end }, beyondHorizon, truncated }
//
//   count         every colliding pair inside the window, not a boolean: two
//                 weekly series collide every week and "there is a conflict" is
//                 not enough for anyone to act on. MMS prints this number.
//   conflicts     the first `sample` of them, in date order, each naming the
//                 proposed occurrence, the existing one it lands on, and who is
//                 double-booked by the pair.
//   beyondHorizon the proposal keeps producing entries past `horizon.end`, so
//                 `count === 0` means "none found before then", not "none".
//   truncated     an expansion hit MAX_OCCURRENCES inside the window. Separate
//                 from beyondHorizon because it means the window itself was not
//                 fully walked.
export function findConflicts(proposed, schedules = [], options = {}) {
  const {
    horizonDays = CONFLICT_HORIZON_DAYS,
    sample = CONFLICT_SAMPLE,
    ignore = proposed?.seriesId ? { seriesId: proposed.seriesId } : null,
  } = options;

  const p = flattenProposed(proposed);
  const f = flattenSchedules(schedules);

  // **`ignoreNothing` is required, not optional**: omitting it throws
  // `missing field ignoreNothing` from inside the wasm glue with no file named.
  // The three states the JavaScript expresses with one nullable object —
  // exclude the proposal's own series, exclude one entry of it, exclude nothing
  // — are three fields here because the boundary takes records of scalars.
  const opts = {
    // **`num()`, not `|| null`.** `horizonDays: 0` is a legitimate value — "the
    // day itself and nothing after" — and `Number(0) || null` is `null`, which
    // the boundary reads as "not passed" and answers with the 365-day default.
    horizonDays: num(horizonDays),
    sample: num(sample),
    ignoreSeriesId: ignore?.seriesId ?? null,
    ignoreRuleDate: ignore?.ruleDate ?? null,
    ignoreNothing: ignore === null,
  };

  const args = [
    p.proposed, p.exceptions, p.studentIds, p.overrides, p.overrideStudents,
    f.series, f.exceptions, f.seriesStudents, f.overrides, f.overrideStudents, opts,
  ];

  // Three calls over one fold, which is the flat rule's answer to returning
  // two lists from one place — see `core-ffi`'s header. The summary is scalars,
  // the sample is the pairs, and who is double-booked is keyed back to the
  // sample by index.
  const summary = coreSync().findConflicts(...args);
  if (summary.count === 0) {
    return rejoin(summary, [], []);
  }
  return rejoin(summary, coreSync().conflictSample(...args), coreSync().conflictDoubleBooked(...args));
}

// The one-day schedule a "this entry only" save proposes.
//
// Its own function because the shape is easy to get subtly wrong: the day it
// carries is the day the form is *showing* — where the lesson will be taught,
// which is where it can collide — while the entry it replaces is identified by
// its rule date, which is what `ignore` needs and is not the same value the
// moment a lesson has been moved.
//
// It takes both halves of the category, though the conflict report echoes
// only the *stored* side of a collision, because this builds a `Schedule` and
// can set every field of it.
export function singleEntrySchedule({
  seriesId = null, date, startTime, durationMin, studentIds = [],
  categoryId = null, categoryLabel = null,
}) {
  return {
    seriesId,
    startDate: date,
    endDate: date,
    startTime,
    durationMin,
    studentIds,
    categoryId,
    categoryLabel,
    frequency: 'weekly',
    interval: 1,
    exceptions: [],
    overrides: {},
  };
}
